import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { Location } from '../types';


type LocationCallback = (location?: Location) => void;
type ErrorCallback = (error: LocationError) => void;

export type LocationError = 'permission_denied' | 'position_unavailable' | 'timeout' | 'unknown';

export class LocationService {
  private locationWatchId: string | null = null;
  private locationCallbacks: LocationCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private static lastKnownLocation: Location | null = null;
  private trackingPollId: number | null = null;
  private hasNotifiedPermissionDenied: boolean = false; // Only notify once per session
  private readonly TRACKING_POLL_INTERVAL = 2000; // 2 seconds between retries
  private readonly LOCATION_TIMEOUT = 5000; // 5 seconds for one-time getCurrentPosition
  private readonly WATCH_TIMEOUT = 3000; // 3 seconds for watchPosition before retry
  private readonly LOCATION_MAX_AGE = 5000; // 5 seconds - accept cached position up to this age
  private readonly MIN_UPDATE_INTERVAL_MS = 2000; // Only process updates every 2 seconds

  constructor() {
    this.onLocationUpdate(LocationService.setLastKnownLocation);
  }

  // Start watching location changes
  public async startTracking(): Promise<void> {
    if (this.locationWatchId) {
      console.log('[LocationService] Watch already active, skipping _startTracking.');
      return;
    }

    try {
      await this._startTracking();
    } catch (error) {
      this.handleLocationError(error);
    }
  }

  private async _startTracking(): Promise<void> {
    if (Capacitor.getPlatform() === 'web') {
      // On web, just try to watch - browser will handle permission prompt
      this.setupWebWatch();
    } else {
      // On native, we need to explicitly request permissions
      const hasPermission = await this.requestNativePermissions();
      if (!hasPermission) {
        throw new Error('Location permission not granted');
      }
      await this.setupNativeWatch();
    }
  }

  private async requestNativePermissions(): Promise<boolean> {
    console.log('[LocationService] Requesting native permissions...');
    const permissionStatus = await Geolocation.requestPermissions();
    return permissionStatus.location === 'granted';
  }

  private async clearWatch(): Promise<void> {
    if (!this.locationWatchId) return;

    if (Capacitor.getPlatform() === 'web') {
      navigator.geolocation.clearWatch(Number(this.locationWatchId));
    } else {
      await Geolocation.clearWatch({ id: this.locationWatchId });
    }
    this.locationWatchId = null;
  }

  private _startTrackingPoll(): void {
    if (this.trackingPollId) return;

    console.log('[LocationService] Starting location tracking poll...');
    this.trackingPollId = window.setInterval(async () => {
      try {
        await this.clearWatch();
        await this._startTracking();
      } catch (error) {
        console.warn('[LocationService] Tracking poll retry failed:', error);
      }
    }, this.TRACKING_POLL_INTERVAL);
  }

  private _stopTrackingPoll(): void {
    if (this.trackingPollId) {
      console.log('[LocationService] Stopping location tracking poll...');
      window.clearInterval(this.trackingPollId);
      this.trackingPollId = null;
    }
  }

  // Stop watching location changes
  public async stopTracking(): Promise<void> {
    await this.clearWatch();
    this._stopTrackingPoll();
  }

  // Get current location (one-time)
  async getCurrentLocation(): Promise<Location> {
    try {
      if (Capacitor.getPlatform() !== 'web') {
        return await this.getLocationNative();
      }
      return await this.getLocationWeb();
    } catch (error) {
      this.handleLocationError(error);
      // Check if we have a fallback location
      const lastLocation = LocationService.getLastKnownLocation();
      if (lastLocation) {
        return lastLocation;
      }
      // If no fallback location, throw the error
      throw new Error('Unable to get current location');
    }
  }

  private async getLocationWeb(): Promise<Location> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      const timeoutId = setTimeout(() => {
        console.log('[LocationService] Location request timed out');
        reject(new Error('Location request timed out'));
      }, this.LOCATION_TIMEOUT);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          resolve(location);
        },
        (error) => {
          clearTimeout(timeoutId);
          console.warn('Geolocation error:', error.code, error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: this.LOCATION_TIMEOUT,
          maximumAge: this.LOCATION_MAX_AGE
        }
      );
    });
  }

  private async getLocationNative(): Promise<Location> {
    const hasPermission = await this.requestNativePermissions();
    if (!hasPermission) {
      throw new Error('Location permission not granted');
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: this.LOCATION_TIMEOUT
    });

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    };
  }

  public static setLastKnownLocation(location?: Location): void {
    LocationService.lastKnownLocation = location || null;
  }

  public static getLastKnownLocation(): Location | null{
    return LocationService.lastKnownLocation || null;
  }

  // Register a callback to receive location updates
  public onLocationUpdate(callback: LocationCallback): void {
    this.locationCallbacks.push(callback);
  }

  // Remove a previously registered callback
  public offLocationUpdate(callback: LocationCallback): void {
    this.locationCallbacks = this.locationCallbacks.filter(cb => cb !== callback);
  }

  // Register a callback to receive location errors
  public onLocationError(callback: ErrorCallback): void {
    this.errorCallbacks.push(callback);
  }

  // Remove a previously registered error callback
  public offLocationError(callback: ErrorCallback): void {
    this.errorCallbacks = this.errorCallbacks.filter(cb => cb !== callback);
  }

  private handleLocationUpdate(location: Location): void {
    // Reset permission denied flag when we successfully get location
    this.hasNotifiedPermissionDenied = false;

    this.locationCallbacks.forEach(callback => {
      try {
        callback(location);
      } catch (error) {
        console.error('Error in location listener:', error);
      }
    });
  }

  private handleLocationError(error: any): void {
    console.error('LocationService error:', error?.code, error?.message, error);

    // Determine error type
    const errorCode = error?.code;
    let errorType: LocationError = 'unknown';

    if (errorCode === 1) {
      errorType = 'permission_denied';
    } else if (errorCode === 2) {
      errorType = 'position_unavailable';
    } else if (errorCode === 3) {
      errorType = 'timeout';
    }

    // Notify error callbacks (only once for permission denied)
    if (errorType === 'permission_denied') {
      if (!this.hasNotifiedPermissionDenied) {
        this.hasNotifiedPermissionDenied = true;
        this.notifyErrorCallbacks(errorType);
      }
    }

    // Start recovery poll for any location error to retry getting location
    console.warn('[LocationService] Location error occurred, starting recovery poll.');
    this._startTrackingPoll();
  }

  private notifyErrorCallbacks(errorType: LocationError): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(errorType);
      } catch (error) {
        console.error('Error in location error listener:', error);
      }
    });
  }

  private async setupNativeWatch(): Promise<void> {
    console.log('[LocationService] setupNativeWatch called.');
    let lastUpdateTime = 0;
    try {
      this.locationWatchId = await Geolocation.watchPosition(
        { enableHighAccuracy: true, maximumAge: 0, timeout: this.WATCH_TIMEOUT },
        (position, error) => {
        if (error) {
          this.handleLocationError(error);
          return;
        }
        if (position) {
          this._stopTrackingPoll(); // Stop poll on first successful callback
          // --- Throttling Logic ---
          const now = Date.now();
          if (now - lastUpdateTime < this.MIN_UPDATE_INTERVAL_MS) {
            return; // Not enough time passed, ignore this update
          }
          lastUpdateTime = now; // Update timestamp for the processed update
          // --- End Throttling ---

          const coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          this.handleLocationUpdate(coordinates);
        }
      }
    );
      console.log(`[LocationService] Native watch started with ID: ${this.locationWatchId}`);
    } catch (error) {
      console.error('[LocationService] Error starting native watch:', error);
      this.locationWatchId = null; // Ensure watchId is null if watch fails to start
      this.handleLocationError(error);
    }
  }

  private setupWebWatch(): void {
    console.log('[LocationService] setupWebWatch called.');
    let lastUpdateTime = 0;

    try {
      // Initiate watch, store ID locally first
      this.locationWatchId = navigator.geolocation.watchPosition(
        position => {
          // Location found. Remove long polling if exists.
          this._stopTrackingPoll();

          // --- Throttling Logic ---
          const now = Date.now();
          if (now - lastUpdateTime < this.MIN_UPDATE_INTERVAL_MS) {
            return; // Not enough time passed
          }
          lastUpdateTime = now;
          // --- End Throttling ---

          const coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          console.log('[LocationService] Web watch processed position update.');
          this.handleLocationUpdate(coordinates);
        },
        error => {
          this.handleLocationError(error);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: this.WATCH_TIMEOUT }
      ) as unknown as string;
    } catch (error) {
      console.error('[LocationService] Error starting web watch:', error);
      this.locationWatchId = null; // Ensure watchId is null if watch fails to start
      this.handleLocationError(error);
    }
  }
}